import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr, mediaUrl, downloadMedia } from "@/lib/apiClient";
import { TOOLS } from "@/config/tools";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Trash2, Sparkles, Zap, X } from "lucide-react";

export default function Library() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null); // full-size image URL to preview
  const limit = 12;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/generations?page=${page}&limit=${limit}&status=completed`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!viewer) return;
    const onKey = (e) => { if (e.key === "Escape") setViewer(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  const del = async (id) => {
    try { await client.delete(`/generations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const download = async (g) => {
    try { await downloadMedia(g.output_urls[0], `adgen-${g.service}-${g.id}`); }
    catch (e) { toast.error(apiErr(e, "Download failed")); }
  };

  const statusColor = { completed: "bg-emerald-500/15 text-emerald-600", failed: "bg-destructive/15 text-destructive", processing: "bg-amber-500/15 text-amber-600", queued: "bg-secondary text-muted-foreground" };
  const pages = Math.ceil(total / limit);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">My Creations</h1>
      <p className="mt-2 text-muted-foreground">{total} generation{total !== 1 ? "s" : ""}</p>

      {loading ? (
        <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">{[...Array(8)].map((_, i) => <div key={i} className="shimmer aspect-square rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border py-20 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="h-7 w-7" /></span>
          <p className="mt-4 font-semibold">Your creative library is empty.</p>
          <p className="text-sm text-muted-foreground">Let's create something amazing.</p>
          <Button className="mt-5 rounded-full" onClick={() => navigate("/create")}>Start Creating</Button>
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            {items.map((g) => (
              <div key={g.id} data-testid={`library-item-${g.id}`} className="studio-card overflow-hidden">
                <button
                  type="button"
                  data-testid={`open-viewer-${g.id}`}
                  onClick={() => g.output_urls?.[0] && setViewer({ url: g.output_urls[0], isVideo: g.media_type === "video" })}
                  className="relative block aspect-square w-full cursor-zoom-in bg-secondary/40"
                >
                  {g.output_urls?.[0] ? (
                    g.media_type === "video" ? (
                      <video
                        src={`${mediaUrl(g.output_urls[0])}#t=0.1`}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.02]"
                      />
                    ) : (
                      <img src={mediaUrl(g.output_urls[0])} alt={g.service} className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.02]" />
                    )
                  ) : (
                    <div className="grid h-full place-items-center text-xs text-muted-foreground capitalize">{g.status}…</div>
                  )}
                  <Badge className={`absolute left-2 top-2 border-0 text-[10px] capitalize ${statusColor[g.status] || ""}`}>{g.status}</Badge>
                </button>
                <div className="p-3">
                  <p className="truncate text-sm font-semibold">{TOOLS[g.service]?.title || g.service}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="capitalize">{g.model}</span> · <Zap className="h-3 w-3 text-primary" fill="currentColor" />{g.credits_charged || g.credits_required} · {new Date(g.created_at).toLocaleDateString()}
                  </p>
                  <div className="mt-2 flex gap-1">
                    {g.output_urls?.[0] && (
                      <Button size="icon" variant="outline" data-testid={`download-${g.id}`} className="h-8 w-8 rounded-lg" onClick={() => download(g)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="outline" className="ml-auto h-8 w-8 rounded-lg text-destructive" data-testid={`delete-${g.id}`} onClick={() => del(g.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {pages > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              <Button variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="grid place-items-center px-4 text-sm text-muted-foreground">Page {page} of {pages}</span>
              <Button variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </>
      )}

      {viewer && (
        <div
          data-testid="image-viewer"
          role="dialog"
          onClick={() => setViewer(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm animate-in fade-in duration-150"
        >
          <button
            type="button"
            data-testid="close-viewer"
            onClick={(e) => { e.stopPropagation(); setViewer(null); }}
            className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={mediaUrl(viewer.url)}
            alt="preview"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
            style={{ display: viewer.isVideo ? "none" : "block" }}
          />
          {viewer.isVideo && (
            <video
              src={mediaUrl(viewer.url)}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
            />
          )}
        </div>
      )}
    </div>
  );
}
